import { OrientationType } from "@lib/titan2/dist/game/renderer/constant/RendererConstants";
import { MeshAttachment, Skin } from "@pixi-spine/all-4.0";
import { gsap } from "gsap";
import { MotionPathPlugin } from "gsap/all";
import { DEG_TO_RAD, Point } from "pixi.js";
gsap.registerPlugin(MotionPathPlugin);
/**
 * Modifies a spine to follow a path.
 *
 * **IMPORTANT: The spine must have a triangle strip mesh attachment.**
 */
export class SpineTrailModifier {
    constructor(spine, referenceLength, skinName) {
        this.spine = spine;
        this.referenceLength = referenceLength;
        // Spines share skins by default, so we need to make the spine use a copy of the skin to avoid modifying other spines with the same skin.
        const originalSkin = (skinName ? spine.spineData.findSkin(skinName) : spine.spineData.defaultSkin);
        this.skin = new Skin(originalSkin.name);
        this.skin.addSkin(originalSkin);
        // Copied skins still share mesh attachments, so we need to make a copies of these too, using a parent mesh so we only need to modify one set of vertices.
        const meshEntries = this.skin.getAttachments().filter(entry => entry.attachment instanceof MeshAttachment);
        const parentMeshIndex = meshEntries.findIndex(entry => !entry.attachment.getParentMesh());
        this.slotIndex = meshEntries[parentMeshIndex].slotIndex;
        this.originalMeshAttachment = meshEntries[parentMeshIndex].attachment;
        this.meshAttachment = this.originalMeshAttachment.copy();
        this.skin.setAttachment(meshEntries[parentMeshIndex].slotIndex, meshEntries[parentMeshIndex].name, this.meshAttachment);
        meshEntries.slice(parentMeshIndex, 1);
        for (const meshEntry of meshEntries) {
            const uniqueChildMesh = meshEntry.attachment.copy();
            uniqueChildMesh.setParentMesh(this.meshAttachment);
            this.skin.setAttachment(meshEntry.slotIndex, meshEntry.name, uniqueChildMesh);
        }
    }
    /**
     * Align the spine to a path through a series of points.
     */
    alignToPath(points, curviness) {
        this.spine.skeleton.skin = this.skin;
        // Create a path from the points.
        const path = MotionPathPlugin.arrayToRawPath(points, { curviness });
        const length = MotionPathPlugin.getLength(path);
        const thickness = this.referenceLength ? length / this.referenceLength : 1;
        // Sequentially align each pair of vertices with the path (compensating for the scale of the root bone).
        const originalVertices = this.computeOriginalVertices();
        const sortedIndices = this.computeSortedIndices(originalVertices);
        const { scaleX, scaleY } = this.spine.spineData.bones[0];
        for (let i = 0; i < originalVertices.length; i += 2) {
            // Get the indices of the current pair of vertices.
            const topIndex = sortedIndices[i];
            const bottomIndex = sortedIndices[i + 1];
            // Sample the curve for the midpoint of the pair, including the angle of the curve at that point.
            const progress = i / (originalVertices.length - 2);
            const { x, y, angle } = MotionPathPlugin.getPositionOnPath(path, progress, true);
            // Calculate the spacing vector from the original vertices, rotated through the sampled angle.
            const spacing = originalVertices[topIndex].y - originalVertices[bottomIndex].y;
            const spacingX = Math.sin(angle * DEG_TO_RAD) * spacing * thickness;
            const spacingY = Math.cos(angle * DEG_TO_RAD) * spacing * thickness;
            // Place the vertices at the sampled position (with a negative y due to the spine's inverted y axis), offset by the spacing vector.
            this.meshAttachment.vertices[topIndex * 2] = (x - spacingX) / scaleX;
            this.meshAttachment.vertices[topIndex * 2 + 1] = (-y - spacingY) / scaleY;
            this.meshAttachment.vertices[bottomIndex * 2] = (x + spacingX) / scaleX;
            this.meshAttachment.vertices[bottomIndex * 2 + 1] = (-y + spacingY) / scaleY;
        }
    }
    /**
     * Compute the vertices from the original mesh attachment.
     */
    computeOriginalVertices() {
        const slot = this.spine.skeleton.slots[this.slotIndex];
        const originalVerticesBuffer = new Float32Array(this.originalMeshAttachment.worldVerticesLength);
        this.originalMeshAttachment.computeWorldVertices(slot, 0, this.meshAttachment.worldVerticesLength, originalVerticesBuffer, 0, 2);
        const originalVertices = [];
        for (let i = 0; i < originalVerticesBuffer.length; i += 2) {
            originalVertices.push(new Point(originalVerticesBuffer[i], originalVerticesBuffer[i + 1]));
        }
        return originalVertices;
    }
    /**
     * Compute sorted indices for a left-to-right, top-to-bottom winding.
     */
    computeSortedIndices(originalVertices) {
        return Array.from(originalVertices.keys()).sort((a, b) => originalVertices[a].x - originalVertices[b].x || originalVertices[a].y - originalVertices[b].y);
    }
}
/**
 * Modifies a TitanSpine to follow a path.
 *
 * **IMPORTANT: The spine must have a triangle strip mesh attachment.**
 */
export class TitanSpineTrailModifier {
    constructor(spine, referenceLength, skinName) {
        // TitanSpine always makes a landscape spine.
        const landscapeModifier = new SpineTrailModifier(spine.getSpine("LANDSCAPE"), referenceLength, skinName);
        // TitanSpine only makes a separate portrait spine if it is deeply different.
        const portraitModifier = spine.hasPortraitProperties
            ? new SpineTrailModifier(spine.getSpine("PORTRAIT"), referenceLength, skinName)
            : landscapeModifier;
        this.modifiers = {
            [OrientationType.LANDSCAPE]: landscapeModifier,
            [OrientationType.PORTRAIT]: portraitModifier,
        };
    }
    /**
     * Align the spine to a path through a series of points.
     */
    alignToPath(points, curviness, orientation) {
        if (orientation) {
            this.modifiers[orientation].alignToPath(points, curviness);
        }
        else {
            this.modifiers[OrientationType.LANDSCAPE].alignToPath(points, curviness);
            this.modifiers[OrientationType.PORTRAIT].alignToPath(points, curviness);
        }
    }
}
//# sourceMappingURL=SpineTrailModifier.js.map